import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import AuthShell from "../components/AuthShell";
import { formatLoginError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [mfa, setMfa] = useState(false);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email, password, code || undefined);
      if (result?.mfa_required) { setMfa(true); return; }
      navigate("/dashboard");
    } catch (err) {
      setError(formatLoginError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to your monitoring dashboard."
      footer={
        <>
          Don't have an account?{" "}
          <Link to="/register" className="font-medium text-[#536d9f] hover:text-[#354e79]" data-testid="go-register-link">
            Create one
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-xs font-semibold text-[#44546c]">Email</Label>
          <Input
            id="email"
            autoComplete="username"
            type="email"
            data-testid="login-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            required
            className="h-11 rounded-xl border-[#dce5f0] bg-[#fbfcfe] focus-visible:ring-[#536d9f]"
          />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-xs font-semibold">Password</Label>
            <Link to="/forgot-password" className="text-xs text-[#536d9f] hover:text-[#354e79]" data-testid="forgot-link">
              Forgot?
            </Link>
          </div>
          <Input
            id="password"
            autoComplete="current-password"
            type="password"
            data-testid="login-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            className="h-11 rounded-xl border-[#dce5f0] bg-[#fbfcfe] focus-visible:ring-[#536d9f]"
          />
        </div>
        {mfa && <div className="space-y-2"><Label htmlFor="login-otp">Authenticator or recovery code</Label><Input id="login-otp" autoComplete="one-time-code" autoFocus value={code} onChange={e=>setCode(e.target.value)} required /><p className="text-xs text-slate-500">Enter the code from your authenticator app, or one of your saved recovery codes.</p></div>}
        {error && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="login-error">
            {error}
          </p>
        )}
        <Button
          type="submit"
          disabled={loading}
          data-testid="login-submit-btn"
          className="h-11 w-full rounded-xl bg-[#536d9f] text-white shadow-[0_5px_15px_rgba(83,109,159,0.18)] transition-all duration-200 hover:bg-[#435c8a] hover:shadow-[0_7px_19px_rgba(83,109,159,0.22)]"
        >
          {loading && <Loader2 size={16} className="mr-2 animate-spin" />}
          Sign in
        </Button>
      </form>
    </AuthShell>
  );
}
